// frontend/src/utils/frame/buildFrameControlState.js

function normalizeText(text = '') {
  return String(text).toLowerCase().trim();
}

function escapeRegExp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function containsWholePhrase(text = '', phrase = '') {
  const normalizedText = normalizeText(text);
  const normalizedPhrase = normalizeText(phrase);

  if (!normalizedText || !normalizedPhrase) return false;

  const pattern = new RegExp(
    `(^|[^a-z0-9])${escapeRegExp(normalizedPhrase)}(?=$|[^a-z0-9])`,
    'i'
  );

  return pattern.test(normalizedText);
}

function detectExplicitOverride(message = '') {
  const lower = normalizeText(message);

  const overridePhrases = [
    'stop',
    'switch',
    'change direction',
    'forget that',
    'never mind',
    'lets do this instead',
    'let’s do this instead',
    'we are doing this now',
    'we are doing this instead',
    'do this instead',
    'start over',
  ];

  return overridePhrases.some((phrase) =>
    containsWholePhrase(lower, phrase)
  );
}

function detectSidebar(message = '') {
  const lower = normalizeText(message);

  const sidebarIndicators = [
    'this file is getting big',
    'we should clean this up',
    'we can fix this later',
    'side note',
    'by the way',
    'not important',
    'later we should',
    'eventually',
  ];

  return sidebarIndicators.some((phrase) =>
    containsWholePhrase(lower, phrase)
  );
}

function detectFreshOpen(message = '') {
  const lower = normalizeText(message).replace(/[.,!?]+$/g, '');

  const openerPhrases = new Set([
    'hi',
    'hello',
    'hey',
    'hiya',
    'yo',
    'hello daisy',
    'hi daisy',
    'hey daisy',
    'good morning',
    'good afternoon',
    'good evening',
    'morning',
    'afternoon',
    'evening',
  ]);

  return openerPhrases.has(lower);
}

function classifyAuthority(message = '') {
  const lower = normalizeText(message);

  if (detectExplicitOverride(lower)) return 'explicit';

  const softDirectives = [
    'we should',
    'maybe we should',
    'i think we should',
    'lets',
    'let’s',
  ];

  if (
    softDirectives.some((phrase) =>
      containsWholePhrase(lower, phrase)
    )
  ) {
    return 'inferred';
  }

  return 'ambient';
}

function estimateDriftDelta({
  isOverrideCandidate = false,
  isSidebar = false,
  isFreshOpen = false,
}) {
  if (isOverrideCandidate) return 'high';
  if (isFreshOpen) return 'medium';
  if (isSidebar) return 'low';
  return 'low';
}

function determineFrameLayer({
  isSidebar = false,
  isOverrideCandidate = false,
  isFreshOpen = false,
}) {
  if (isFreshOpen) return 'reset';
  if (isSidebar) return 'sidebar';
  if (isOverrideCandidate) return 'governing';
  return 'execution';
}

function buildFrameControlState({
  message = '',
  currentFrame = null,
} = {}) {
  const normalized = normalizeText(message);

  const isFreshOpen = detectFreshOpen(normalized);
  const isSidebar = isFreshOpen
    ? false
    : detectSidebar(normalized);
  const isOverrideCandidate = isFreshOpen
    ? false
    : detectExplicitOverride(normalized);
  const authority = isFreshOpen
    ? 'ambient'
    : classifyAuthority(normalized);

  const frameLayer = determineFrameLayer({
    isSidebar,
    isOverrideCandidate,
    isFreshOpen,
  });

  const driftDelta = estimateDriftDelta({
    isOverrideCandidate,
    isSidebar,
    isFreshOpen,
  });

  const requiresConfirmation =
    frameLayer === 'governing' && authority === 'explicit';

  const shouldResetEphemeral = isFreshOpen;
  const shouldClearPendingQuestion = isFreshOpen;
  const shouldClearLastReferencedTopic = isFreshOpen;
  const shouldDeweightCarryForward = isFreshOpen;
  const shouldClearFrameLock = isFreshOpen;

  return {
    frameLayer, // reset | governing | execution | sidebar
    authority, // explicit | inferred | ambient
    isOverrideCandidate,
    requiresConfirmation,
    driftDelta, // low | medium | high
    isSidebar,
    isFreshOpen,
    shouldResetEphemeral,
    shouldClearPendingQuestion,
    shouldClearLastReferencedTopic,
    shouldDeweightCarryForward,
    shouldClearFrameLock,
    currentFrameSnapshot: currentFrame || null,
  };
}

export default buildFrameControlState;